import { CompanyRole } from 'constants/CompanyRole';
import { toNumber } from 'lodash';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { getJobApplicantFeature } from 'store/actions/job-applicant-link-actions';
import {
  jobApplicantFeatureCompanyIdSelector,
  jobApplicantFeatureEnabledSelector,
} from 'store/selectors/job-applicant-link-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';

/**
 * Roles the JobApplicants endpoints accept. ACCOUNT_MANAGER and EMPLOYEE are excluded, so
 * every applicant surface gates the same way the API does instead of discovering a 403.
 *
 * Note this is deliberately NOT the `isManagerRole` predicate in MenuItemsByRole — that one
 * excludes ADMIN, which the JobApplicants endpoints allow.
 */
const ALLOWED_ROLES = [
  CompanyRole.ADMIN,
  CompanyRole.COMPANY_MANAGER,
  CompanyRole.HR_MANAGER,
  CompanyRole.LOCATION_MANAGER,
];

/**
 * True when the given company has Job Applicants switched on AND the signed-in user holds a
 * role allowed to manage application links.
 *
 * The flag has two sources, because the profile payload only describes a company for users
 * scoped to one:
 *
 * - Managers — `featureJobApplicantsEnabled` on /Profile. Changes on next app reload.
 * - Admins — profile carries no company (`profile.companyId` is null and the company comes
 *   from the route), so the profile flag says nothing about the company being viewed. Resolved
 *   instead from the admin-only GET /JobApplicants/Feature/{companyId}.
 *
 * Pass the companyId the surface is operating on; without it an admin resolves to false.
 */
const useJobApplicantsEnabled = (companyId?: string | number): boolean => {
  const dispatch = useAppDispatch();
  const profile = useAppSelector(profileSelector);
  const featureCompanyId = useAppSelector(jobApplicantFeatureCompanyIdSelector);
  const featureEnabled = useAppSelector(jobApplicantFeatureEnabledSelector);

  const isAdmin = profile?.role === CompanyRole.ADMIN;
  const requestedCompanyId = companyId ? toNumber(companyId) : null;

  useEffect(() => {
    if (isAdmin && requestedCompanyId) dispatch(getJobApplicantFeature(requestedCompanyId));
  }, [dispatch, isAdmin, requestedCompanyId]);

  if (!profile || !ALLOWED_ROLES.includes(profile.role)) return false;

  if (isAdmin) {
    // Only trust the fetched value when it belongs to the company being viewed, so a value
    // left over from a previously opened company can't leak in.
    return featureCompanyId !== null && featureCompanyId === requestedCompanyId && featureEnabled;
  }

  return !!profile.featureJobApplicantsEnabled;
};

export default useJobApplicantsEnabled;
